import React, { useState, useRef, useEffect, useMemo } from "react";
import { renderToStaticMarkup, renderToString } from "react-dom/server";
import { useLocation, useNavigate, useParams } from "react-router-dom";
//external imports
import DOMPurify from "dompurify";
//internal imports
import "./answer/Answer.module.css";
import ChatCard from "./chat/ChatCard";
import { parseAnswerToHtml } from "./answer/AnswerParser";
import { TextAreaWithButton } from "./TextAreaWithButton";
import { Button } from "../../design-system/Button/Button";
import { thoughtProcessRepo } from "../../data/thoughtProcessRepo";
// import { checkFile, generateChat, getFileChats } from "../../api";
import ShowCitationSource from "./chat/ShowCitationSource";
import { BaseURL } from "../../utils/appConstant";
import ErrorBoundary from "../../utils/ErrorBoundry";
import { delete_user_data_from_client_side, get_user_data, isValidEpochTime } from "../../utils/common";
import NoPage from "../NoPage";
import { FaS } from "react-icons/fa6";
import { isTokenExpired } from "../layout/Layout";
import useRefreshToken from "../../utils/useRefreshToken";
import { checkFile, generateChat, getFileChats } from "../../api/axiosApi";

interface ThoughtsProps {
    prompt: string;
    response: string | string[] | JSX.Element;
    citations?: string | string[] | JSX.Element[] | { text: string; link: string } | { text: string; link: string }[];
}

type HtmlParsedAnswer = {
    answerHtml: JSX.Element[] | string;
    citations: string[];
};
const SideArea: React.FC = () => {
    return (
        <div className="mt-20 ml-8">
            <Button text="Generate New" color="bg-green-700 text-white hover:bg-green-900" />
        </div>
    );
};

const RenderHTML = ({ html }: any): JSX.Element => {
    const head = "<html><body>";
    const tail = "</body></html>";
    html = head + html + tail;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

interface GeneratedChatListProps {
    generatedChat: JSX.Element[];
}
const GeneratedChatList = ({ generatedChat }: GeneratedChatListProps) => {
    return <div className=" items-center justify-center mb-28 w-full ">{generatedChat.map((chat, index) => React.cloneElement(chat, { key: index }))}</div>;
};

const Thoughts = ({ prompt: initialPrompt, response, citations }: ThoughtsProps) => {
    const [isError, setIsError] = useState(true);

    const navigate = useNavigate();
    const unclassified = "Unclassified";
    const caseletCategory = "Caselet";
    const [prompt, setPrompt] = useState(initialPrompt);
    const [generatedChat, setGeneratedChat] = useState<JSX.Element[]>([]);
    const location = useLocation();
    const lastCardRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [err, setErr] = useState("");
    const { Id } = useParams();
    const [openRight, setOpenRight] = useState(false);
    const [fileName, setFileName] = useState<string>();
    const refreshToken = useRefreshToken();
    function sortCitationWithLink(
        citation: string[],
        citationWithLink: { citation: string; link: string; author: string; title: string }[]
    ): { citation: string; link: string; author: string; title: string }[] {
        const sortedIndices: number[] = [];

        // Find the indices of matched citations
        for (let i = 0; i < citation.length; i++) {
            const foundIndex = citationWithLink.findIndex(item => item.citation.includes(citation[i]));
            if (foundIndex !== -1) {
                sortedIndices.push(foundIndex);
            }
        }

        // Sort citationWithLink based on the found indices
        return citationWithLink.sort((a, b) => {
            const aIndex = sortedIndices.find(i => citationWithLink[i].citation === a.citation) || -1;
            const bIndex = sortedIndices.find(i => citationWithLink[i].citation === b.citation) || -1;
            if (aIndex !== -1 && bIndex !== -1) {
                const aIndexNum = sortedIndices.indexOf(aIndex) ?? -1;
                const bIndexNum = sortedIndices.indexOf(bIndex) ?? -1;

                if (aIndexNum > bIndexNum) return 1;
                if (bIndexNum > aIndexNum) return -1;
                return 0;
            } else return -1;
        });
    }
    function filterAndSortCitationWithLink(
        citations: string[],
        citationWithLink: { citation: string; link: string; author: string; title: string }[]
    ): { citation: string; link: string; author: string; title: string }[] {
        // Extract filenames from citations
        const citationFilenames = citations.map(citation => citation.split("/").pop() || "");

        // Filter and sort citationWithLink based on citations
        return citationFilenames.reduce(
            (acc, filename) => {
                const match = citationWithLink.find(item => item.citation.endsWith(filename));
                return match ? [...acc, match] : acc;
            },
            [] as typeof citationWithLink
        );
    }

    const openDrawerRight = () => {
        setOpenRight(true);
    };
    const closeDrawerRight = () => {
        setOpenRight(false);
        setShowCitationSource(false);
    };
    const [showCitationSource, setShowCitationSource] = useState(false);

    function onCitationClicked(path: string) {
        setFileName(path);
        setShowCitationSource(true);
        openDrawerRight();
    }
    const handleCitationClick = (path: string) => {
        setShowCitationSource(true);
        openDrawerRight();
    };
    useEffect(() => {
        setShowCitationSource(false);
    }, [setShowCitationSource]);

    async function fetchAndSetThoughts(id: string | undefined) {
        try {
            const checkFileStatus = await checkFile(id);
            const fileExist = await checkFileStatus.data;
            if (!fileExist.status) {
                return;
            }
            const historyResponse = await getFileChats(id);
            if (historyResponse == null) {
                throw new Error();
            }

            const data = await historyResponse.data;
            setIsError(true);
            for (const item of data) {
                const citationWithLink = item.citations;
                const { answerHtml, citations } = parseAnswerToHtml(item.response, onCitationClicked, citationWithLink);
                const sortedCitationWithLink = filterAndSortCitationWithLink(citations, citationWithLink);
                const filteredCitations = sortedCitationWithLink
                    .filter((citationObj: any) => citations.includes(citationObj.citation.split("/").pop()))
                    .map((item: any) => {
                        const lastPart = item.citation.split("/").pop() || "";
                        return {
                            citation: lastPart,
                            link: item.link,
                            title: item.title,
                            author: item.author
                        };
                    });
                if (item.category === unclassified) {
                    const newChat = (
                        <ChatCard
                            key={generatedChat.length}
                            prompt={item.query}
                            response={answerHtml}
                            citations={citationWithLink}
                            flagForCaselet={false}
                            responseForFineTune={item.response}
                            conversation_id={id}
                            onCitationClick={onCitationClicked}
                            from={"thoughts"}
                            isOldInteractions={false}
                        />
                    );
                    setGeneratedChat(prevChat => [...prevChat, newChat]);
                } else {
                    const newChat = (
                        <ChatCard
                            key={generateChat.length}
                            prompt={item.query}
                            response={answerHtml}
                            citations={citationWithLink}
                            flagForCaselet={true}
                            responseForFineTune={item.response}
                            conversation_id={id}
                            onCitationClick={onCitationClicked}
                            from={"thoughts"}
                            isOldInteractions={false}
                        />
                    );
                    setGeneratedChat(prevChat => [...prevChat, newChat]);
                }
            }
        } catch (err) {
        } finally {
            setIsLoading(false);
        }
    }
    //this method is used to generate the chats using Openai and set in card
    const fetchDataAndGenerateCard = async (id: string | undefined) => {
        const error = false;
        try {
            setIsLoading(true); // Set loading to true when starting the fetch
            const queryResponse = await generateChat(id, prompt);
            const result = await queryResponse.data;
            const citationWithLink = result.citations;
            if (result.category === unclassified) {
                let { answerHtml, citations } = parseAnswerToHtml(result.response, onCitationClicked, citationWithLink);
                const filteredCitations = citationWithLink
                    .filter((citationObj: any) => citations.includes(citationObj.citation.split("/").pop()))
                    .map((item: any) => {
                        const lastPart = item.citation.split("/").pop() || "";
                        return {
                            citation: lastPart,
                            link: item.link,
                            title: item.title,
                            author: item.author
                        };
                    });
                // citations = filteredCitations;
                // const sanitizedAnswerHtml = DOMPurify.sanitize(renderToString(<React.Fragment>{answerHtml}</React.Fragment>));
                const updatedCard = (
                    <ChatCard
                        key={generatedChat.length}
                        prompt={prompt}
                        response={answerHtml}
                        citations={citationWithLink}
                        flagForCaselet={false}
                        responseForFineTune={result.response}
                        conversation_id={id}
                        onCitationClick={onCitationClicked}
                        from={"thoughts"}
                        isOldInteractions={true}
                    />
                );
                setGeneratedChat(prevChat => {
                    const updatedChat = [...prevChat];
                    updatedChat[updatedChat.length - 1] = updatedCard;
                    return updatedChat;
                });
            }
            if (result.category === caseletCategory) {
                const { answerHtml, citations } = parseAnswerToHtml(result.response, onCitationClicked, citationWithLink);
                const sortedCitationWithLink = filterAndSortCitationWithLink(citations, citationWithLink);

                const filteredCitations = sortedCitationWithLink
                    .filter((citationObj: any) => citations.includes(citationObj.citation.split("/").pop()))
                    .map((item: any) => {
                        const lastPart = item.citation.split("/").pop() || "";
                        return {
                            citation: lastPart,
                            link: item.link,
                            title: item.title,
                            author: item.author
                        };
                    });

                const updatedCard = (
                    <ChatCard
                        key={generatedChat.length}
                        prompt={prompt}
                        response={answerHtml}
                        citations={citationWithLink}
                        flagForCaselet={true}
                        responseForFineTune={result.response}
                        conversation_id={id}
                        onCitationClick={onCitationClicked}
                        from={"thoughts"}
                        isOldInteractions={true}
                    />
                );
                setGeneratedChat(prevCaselets => {
                    const updatedCaselets = [...prevCaselets];
                    updatedCaselets[updatedCaselets.length - 1] = updatedCard;
                    return updatedCaselets;
                });
            }
        } catch (err) {
            const errorCard = (
                <ChatCard
                    key={generatedChat.length}
                    prompt={prompt}
                    response={"<div className='flex  justify-center h-44 w-full font-bold p-6'>Oops, something went wrong!  </div>"}
                    citations={citations}
                    flagForCaselet={false}
                    from={"thoughts"}
                    isOldInteractions={true}
                    />
            );
            setGeneratedChat(prevChat => {
                const updatedChat = [...prevChat];
                updatedChat[updatedChat.length - 1] = errorCard;
                return updatedChat;
            });
        } finally {
            setIsLoading(false); // Set loading to false after completing the fetch, regardless of success or failure
        }
    };
    useEffect(() => {
        const from = location.state?.from ?? "individual";
        // console.log("from in useEffect: ", from)
        if (from === "individual") {
            setGeneratedChat([]);
            if (isValidEpochTime(Id)) {
                fetchAndSetThoughts(Id);
            } else {
                setIsError(false);
            }
        } else if (from === "new") {
            setGeneratedChat([]);

            if (isValidEpochTime(Id)) {
                fetchAndSetThoughts(Id);
            } else {
                setIsError(false);
            }
        }
    }, [location.pathname]);
    useEffect(() => {
        if (lastCardRef.current) {
            lastCardRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [generatedChat]);

    const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPrompt(e.target.value);
    };

    const generateResponse = async () => {
        // Create an initial empty card with the current prompt
        const initialEmptyCard = <ChatCard key={generatedChat.length} prompt={prompt} response="" citations={[]} flagForCaselet={false} from={"thoughts"} isOldInteractions={true} />;

        // Update the generatedCaselets state with the initial empty card
        setGeneratedChat(prevCard => [...prevCard, initialEmptyCard]);

        // Clear the prompt
        setPrompt("");
        let from = location.state?.from ?? "individual";
        console.log("from: ", from)
        if (get_user_data() !== null && get_user_data() !== undefined) {
            if (from === "new") {
                try {
                    const checkFileStatus = await checkFile(Id);
                    const fileExist = await checkFileStatus.data;
                    console.log("fileExist: ", fileExist)
                    // const historyResponse = await getFileChats(Id);
                    // if (!historyResponse) {
                    //     throw new Error(`Error! status: ${historyResponse.status}`);
                    // }
                    // console.log("fetchDataAndGenerateCard in try: ", Id);
                    await fetchDataAndGenerateCard(Id);
                    from = false;
                } catch {
                }
            } else {
                if (isValidEpochTime(Id)) {
                    await fetchDataAndGenerateCard(Id);
                } else {
                    setIsError(false);
                }
            }
        } else {
            navigate("/user_login");
        }
    };

    return (
        <div className="">
            {isError ? (
                <ErrorBoundary fallback="Data not found">
                    <div>
                        <div className="shrink-0 w-full pb-6">
                            <GeneratedChatList generatedChat={generatedChat} />
                            {/* <div ref={lastCardRef} /> */}
                        </div>
                        <div className="">
                            <TextAreaWithButton prompt={prompt} onChange={handlePromptChange} onGenerateResponse={generateResponse} isLoading={isLoading} />
                        </div>
                    </div>
                    {showCitationSource && (
                        <div className="citation-source flex-shrink-0">
                            <ShowCitationSource style={{ flexGrow: 1 }} open={openRight} onClose={closeDrawerRight} fileName={fileName} />
                        </div>
                    )}
                </ErrorBoundary>
            ) : (
                <div className="flex items-center justify-center w-full ">
                    <NoPage />
                </div>
            )}
        </div>
    );
};

export default Thoughts;
